import { useState, useEffect } from "react"
import { Users, Edit, Trash2, Search, Mail, Phone, Calendar, X, Save, UserPlus } from "lucide-react"
import Wrapper from "../Wrapper"
import Input from "../../components/Input"

const Dashboard = () => {
  const [admins, setAdmins] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [editingAdmin, setEditingAdmin] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState({ type: "", text: "" })
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    photo: "",
    role: "admin",
    phoneNumber: "",
    isActive: true,
    privileges: ["Dashboard"],
    dutyTiming: {
      startTime: "10:00",
      endTime: "09:00",
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    },
  })

  // Fetch admins data from API
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        setIsLoading(true)
        const response = await fetch("https://quran-backend-theta.vercel.app/v1/user/admins")
        const data = await response.json()

        const transformedAdmins = data.map(admin => ({
          id: admin._id,
          name: admin.name,
          email: admin.email,
          password: admin.password,
          photo: admin.photo,
          role: admin.role,
          phoneNumber: admin.phoneNumber,
          isActive: admin.isActive !== undefined ? admin.isActive : true,
          privileges: admin.privileges || [],
          dutyTiming: admin.dutyTiming || {
            startTime: "10:00",
            endTime: "09:00",
            days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
          }
        }))

        setAdmins(transformedAdmins)
      } catch (error) {
        console.error("Error fetching admins:", error)
        setMessage({ type: "error", text: "Failed to fetch admins" })
      } finally {
        setIsLoading(false)
      }
    }

    fetchAdmins()
  }, [])

  const roles = ["admin", "super-admin", "moderator"]
  const availablePrivileges = [
    "Dashboard",
    "Course",
    "Teacher",
    "Job",
    "Fee",
    "Complete",
    "Attendence",
    "Gallery",
    "Settings",
    "Contact",
  ]
  const timeSlots = [
    "00:00", "01:00", "02:00", "03:00", "04:00", "05:00",
    "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
    "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
    "18:00", "19:00", "20:00", "21:00", "22:00", "23:00","24:00"
  ]
  const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

  const filteredAdmins = admins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.role.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const isValidPassword = (password) => {
    return password.length >= 6
  }

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      photo: "",
      role: "admin",
      phoneNumber: "",
      isActive: true,
      privileges: ["Dashboard"],
      dutyTiming: {
        startTime: "10:00",
        endTime: "09:00",
        days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      },
    })
    setEditingAdmin(null)
    setMessage({ type: "", text: "" })
  }

  const handleAddAdmin = () => {
    resetForm()
    setShowModal(true)
  }

  const handleEditAdmin = (admin) => {
    setFormData({
      name: admin.name,
      email: admin.email,
      password: admin.password,
      photo: admin.photo,
      role: admin.role,
      phoneNumber: admin.phoneNumber,
      isActive: admin.isActive !== undefined ? admin.isActive : true,

      privileges: admin.privileges,
      dutyTiming: admin.dutyTiming,
    })
    setEditingAdmin(admin)
    setMessage({ type: "", text: "" })
    setShowModal(true)
  }

  // New function to handle update with logging
  const handleUpdateAdmin = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: "error", text: "Name is required" });
      return;
    }

    if (!formData.email.trim()) {
      setMessage({ type: "error", text: "Email is required" });
      return;
    }

    if (!isValidEmail(formData.email)) {
      setMessage({ type: "error", text: "Please enter a valid email address" });
      return;
    }

    if (!formData.password.trim()) {
      setMessage({ type: "error", text: "Password is required" });
      return;
    }

    if (!isValidPassword(formData.password)) {
      setMessage({ type: "error", text: "Password must be at least 6 characters long" });
      return;
    }

    if (!formData.phoneNumber.trim()) {
      setMessage({ type: "error", text: "Phone number is required" });
      return;
    }

    if (!formData.privileges || formData.privileges.length === 0) {
      setMessage({ type: "error", text: "At least one privilege must be selected" });
      return;
    }

    if (!formData.dutyTiming.days || formData.dutyTiming.days.length === 0) {
      setMessage({ type: "error", text: "At least one working day must be selected" });
      return;
    }

    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        photo: formData.photo || "example.png",
        role: formData.role,
        phoneNumber: formData.phoneNumber,
        privileges: formData.privileges,
        isActive: formData.isActive,
        dutyTiming: {
          startTime: formData.dutyTiming.startTime,
          endTime: formData.dutyTiming.endTime,
          days: formData.dutyTiming.days,
        },
      });

      const requestOptions = {
        method: "PATCH",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        `https://quran-backend-theta.vercel.app/v1/user/update-user/${editingAdmin.id}`,
        requestOptions
      );
      const result = await response.json();

      if (result.success) {
        setMessage({ type: "success", text: "Admin updated successfully!" });

        // Update the local state with the new data
        setAdmins(admins.map(admin =>
          admin.id === editingAdmin.id ? { ...admin, ...formData } : admin
        ));

        setTimeout(() => {
          setShowModal(false);
          resetForm();
          location.reload()
        }, 2000);
      } else {
        setMessage({ type: "error", text: result.error || "Update failed" });
      }
    } catch (error) {
      console.error("Error updating admin:", error);
      setMessage({ type: "error", text: "Network error. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };
  const handleSaveAdmin = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: "error", text: "Name is required" })
      return
    }

    if (!formData.email.trim()) {
      setMessage({ type: "error", text: "Email is required" })
      return
    }

    if (!isValidEmail(formData.email)) {
      setMessage({ type: "error", text: "Please enter a valid email address" })
      return
    }

    if (!formData.password.trim()) {
      setMessage({ type: "error", text: "Password is required" })
      return
    }

    if (!isValidPassword(formData.password)) {
      setMessage({ type: "error", text: "Password must be at least 6 characters long" })
      return
    }

    if (!formData.phoneNumber.trim()) {
      setMessage({ type: "error", text: "Phone number is required" })
      return
    }

    if (!formData.privileges || formData.privileges.length === 0) {
      setMessage({ type: "error", text: "At least one privilege must be selected" })
      return
    }

    if (!formData.dutyTiming.days || formData.dutyTiming.days.length === 0) {
      setMessage({ type: "error", text: "At least one working day must be selected" })
      return
    }

    setIsLoading(true)
    setMessage({ type: "", text: "" })

    try {
      const myHeaders = new Headers()
      myHeaders.append("Content-Type", "application/json")

      const raw = JSON.stringify({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        photo: "example.png",
        role: "admin",
        phoneNumber: formData.phoneNumber,
        isActive: formData.isActive,
        privileges: formData.privileges,
        dutyTiming: {
          startTime: formData.dutyTiming.startTime,
          endTime: formData.dutyTiming.endTime,
          days: formData.dutyTiming.days,
        },
      })

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      }

      const response = await fetch("https://quran-backend-theta.vercel.app/v1/user/register", requestOptions)
      const result = await response.json()

      if (result.success === true) {
        setMessage({ type: "success", text: "Admin registered successfully!" })
        const refreshResponse = await fetch("https://quran-backend-theta.vercel.app/v1/user/admins")
        const refreshData = await refreshResponse.json()
        const transformedAdmins = refreshData.map(admin => ({
          id: admin._id,
          name: admin.name,
          email: admin.email,
          password: admin.password,
          photo: admin.photo,
          role: admin.role,
          phoneNumber: admin.phoneNumber,
          isActive: admin.isActive !== undefined ? admin.isActive : true,
          privileges: admin.privileges || [],
          dutyTiming: admin.dutyTiming || {
            startTime: "10:00",
            endTime: "09:00",
            days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
          }
        }))
        setAdmins(transformedAdmins)

        setTimeout(() => {
          setShowModal(false)
          resetForm()
          location.reload()
        }, 2000)
      } else {
        setMessage({ type: "error", text: result.error || "Registration failed" })
      }
    } catch (error) {
      console.error(error)
      setMessage({ type: "error", text: "Network error. Please try again." })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAdmin = async (adminId) => {
    console.log("Delete button clicked for admin ID:", adminId);

    if (window.confirm("Are you sure you want to delete this admin?")) {
      try {
        setIsLoading(true);

        const requestOptions = {
          method: "DELETE",
          redirect: "follow"
        };

        const response = await fetch(
          `https://quran-backend-theta.vercel.app/v1/user/delete-user/${adminId}`,
          requestOptions
        );

        const result = await response.json();

        if (result.success) {
          setAdmins(admins.filter((admin) => admin.id !== adminId));
          setMessage({ type: "success", text: "Admin deleted successfully" });
        } else {
          setMessage({ type: "error", text: result.message || "Failed to delete admin" });
        }
      } catch (error) {
        console.error("Error deleting admin:", error);
        setMessage({ type: "error", text: "Failed to delete admin. Please try again." });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (message.text) {
      setMessage({ type: "", text: "" })
    }
  }

  const handlePrivilegeChange = (privilege) => {
    setFormData((prev) => ({
      ...prev,
      privileges: prev.privileges.includes(privilege)
        ? prev.privileges.filter((p) => p !== privilege)
        : [...prev.privileges, privilege],
    }))
    if (message.text) {
      setMessage({ type: "", text: "" })
    }
  }

  const handleDayChange = (day) => {
    setFormData((prev) => ({
      ...prev,
      dutyTiming: {
        ...prev.dutyTiming,
        days: prev.dutyTiming.days.includes(day)
          ? prev.dutyTiming.days.filter((d) => d !== day)
          : [...prev.dutyTiming.days, day],
      },
    }))
    if (message.text) {
      setMessage({ type: "", text: "" })
    }
  }

  const handleDutyTimingChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      dutyTiming: {
        ...prev.dutyTiming,
        [field]: value,
      },
    }))
  }

  return (
    <Wrapper
      searchBar={
        <Input
          placeholder={`Search by name or course...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          Icon={
            <img
              src={require("../../assets/image/Search-Magnifier.png") || "/placeholder.svg"}
              className=" w-8"
              alt=""
            />
          }
          className={" w-full bg-white  pl-12"}
        />
      }
    >
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-gray-50 to-blue-50 p-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#FC7C02] p-3 rounded-xl">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-[#002D42]">Admin Management</h1>
                  <p className="text-gray-600">Manage your admin users and permissions</p>
                </div>
              </div>
              <button
                onClick={handleAddAdmin}
                className="bg-[#FC7C02] hover:bg-[#e56d02] text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <UserPlus className="w-5 h-5" />
                Add New Admin
              </button>
            </div>
          </div>

          {/* Search and Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {/* Search Bar */}
            <div className="md:col-span-2 bg-white rounded-2xl shadow-lg p-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search admins by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FC7C02] focus:border-transparent outline-none transition-all duration-200"
                />
              </div>
            </div>

            {/* Stats Cards */}
            <div className="bg-[#002D42] rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-300">Total Admins</p>
                  <p className="text-2xl font-bold">{admins.length}</p>
                </div>
                <Users className="w-8 h-8 text-gray-400" />
              </div>
            </div>

            <div className="bg-[#FC7C02] rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100">Admin Roles</p>
                  <p className="text-2xl font-bold">{new Set(admins.map((admin) => admin.role)).size}</p>
                </div>
                <div className="w-8 h-8 bg-orange-300 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin List */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-[#002D42]">Admin List</h2>
              <p className="text-gray-600">
                Showing {filteredAdmins.length} of {admins.length} admins
              </p>
            </div>

            {/* Loading state */}
            {isLoading && admins.length === 0 && (
              <div className="p-12 text-center">
                <div className="w-16 h-16 border-4 border-[#FC7C02] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Loading admins...</h3>
              </div>
            )}

            {/* Desktop Table View */}
            {!isLoading && (
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Admin</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Contact</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Role & Privileges</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Duty Timing</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Password</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredAdmins.map((admin) => (
                      <tr key={admin.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {admin.photo ? (
                              <img
                                src={admin.photo}
                                alt={admin.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-r from-[#FC7C02] to-[#002D42] rounded-full flex items-center justify-center text-white font-semibold">
                                {admin.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-[#002D42]">{admin.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-4 h-4" />
                              {admin.email}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-4 h-4" />
                              {admin.phoneNumber}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-[#FC7C02]">
                              {admin.role}
                            </span>
                            <p className="text-sm text-gray-600">{admin.privileges.join(", ") || "No privileges"}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-gray-600">
                              {admin.dutyTiming.startTime} - {admin.dutyTiming.endTime}
                            </p>
                            <p className="text-xs text-gray-500">{admin.dutyTiming.days.length} days/week</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-gray-600">
                              {admin.password}
                            </p>
                            <p className="text-xs text-gray-500">{admin.dutyTiming.days.length} days/week</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditAdmin(admin)}
                              className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors duration-200"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAdmin(admin.id)}
                              className="bg-red-500 hover:bg-red-600 text-red p-2 rounded-lg transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile Card View */}
            {!isLoading && (
              <div className="lg:hidden p-4 space-y-4">
                {filteredAdmins.map((admin) => (
                  <div key={admin.id} className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {admin.photo ? (
                          <img
                            src={admin.photo}
                            alt={admin.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-r from-[#FC7C02] to-[#002D42] rounded-full flex items-center justify-center text-white font-semibold">
                            {admin.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-[#002D42]">{admin.name}</p>
                        </div>
                      </div>
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-[#FC7C02]">
                        {admin.role}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        {admin.email}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        {admin.phoneNumber}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        {admin.dutyTiming.startTime} - {admin.dutyTiming.endTime}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">{admin.privileges.join(", ") || "No privileges"}</p>
                        <p className="text-xs text-gray-500">{admin.dutyTiming.days.length} working days</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditAdmin(admin)}
                          className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors duration-200"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAdmin(admin.id)}
                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors duration-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && filteredAdmins.length === 0 && (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No admins found</h3>
                <p className="text-gray-500">Try adjusting your search criteria or add a new admin.</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-[#002D42]">
                    {editingAdmin ? "Edit Admin" : "Add New Admin"}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    disabled={isLoading}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-50"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {message.text && (
                <div
                  className={`mx-6 mt-4 p-4 rounded-lg ${message.type === "success"
                    ? "bg-green-50 border border-green-200 text-green-800"
                    : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                >
                  <div className="flex items-center gap-2">
                    {message.type === "success" ? (
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    ) : (
                      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <X className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <span className="font-medium">{message.text}</span>
                  </div>
                </div>
              )}

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#002D42] mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FC7C02] focus:border-transparent outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#002D42] mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FC7C02] focus:border-transparent outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#002D42] mb-2">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FC7C02] focus:border-transparent outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter password (min 6 characters)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#002D42] mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FC7C02] focus:border-transparent outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-[#002D42]">Active Status</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="sr-only peer"
                      disabled={isLoading}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FC7C02]"></div>
                    <span className="ml-3 text-sm text-gray-700">
                      {formData.isActive ? "Active" : "Inactive"}
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#002D42] mb-2">Privileges *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {availablePrivileges.map((privilege) => (
                      <label key={privilege} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.privileges.includes(privilege)}
                          onChange={() => handlePrivilegeChange(privilege)}
                          disabled={isLoading}
                          className="rounded border-gray-300 text-[#FC7C02] focus:ring-[#FC7C02] disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-700">{privilege}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#002D42] mb-2">Duty Timing</label>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                        <select
                          value={formData.dutyTiming.startTime}
                          onChange={(e) => handleDutyTimingChange("startTime", e.target.value)}
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#FC7C02] focus:border-transparent outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">End Time</label>
                        <select
                          value={formData.dutyTiming.endTime}
                          onChange={(e) => handleDutyTimingChange("endTime", e.target.value)}
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#FC7C02] focus:border-transparent outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-2">Working Days *</label>
                      <div className="grid grid-cols-2 gap-2">
                        {weekDays.map((day) => (
                          <label key={day} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.dutyTiming.days.includes(day)}
                              onChange={() => handleDayChange(day)}
                              disabled={isLoading}
                              className="rounded border-gray-300 text-[#FC7C02] focus:ring-[#FC7C02] disabled:opacity-50"
                            />
                            <span className="text-sm text-gray-700">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={editingAdmin ? handleUpdateAdmin : handleSaveAdmin}
                  disabled={isLoading}
                  className={`flex-1 ${isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#FC7C02] to-[#002D42] hover:from-[#e56d02] hover:to-[#001a2e]"
                    } text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-200`}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {editingAdmin ? "Updating..." : "Registering..."}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingAdmin ? "Update" : "Create"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  )
}

export default Dashboard